import { Controller } from "@hotwired/stimulus"
import pageMessageBroker from "../page_message_broker"

// Connects to data-controller="claims-experience-selection-modal"
export default class extends Controller {
  static targets = ["modal", "searchInput", "claimsExperiencesList", "selectionSummary", "addButton"]
  static values = {
    workspaceId: String,
    renewalId: String
  }

  connect() {
    console.log("ClaimsExperienceSelectionModalController connected")
    this.currentPage = 1
    this.currentSearch = ""
    this.selectedClaimsExperienceIds = new Set()

    // Listen for modal open events using the page message broker
    this.subscription = pageMessageBroker.subscribe("open-claims-experience-modal", this.handleModalOpen.bind(this))
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleModalOpen(event) {
    console.log("Received open-claims-experience-modal event", event.detail)
    this.renewalIdValue = event.detail.renewalId
    this.open()
  }

  // Open the modal and load claims experiences
  open() {
    this.showModal()
    this.loadClaimsExperiences()
  }

  // Close the modal
  close() {
    this.hideModal()
    this.resetModal()
  }

  // Handle search input
  search(event) {
    event.preventDefault()
    this.currentSearch = this.searchInputTarget.value.trim()
    this.currentPage = 1
    this.loadClaimsExperiences()
  }

  // Handle pagination
  goToPage(event) {
    event.preventDefault()
    this.currentPage = parseInt(event.target.dataset.page)
    this.loadClaimsExperiences()
  }

  // Handle claims experience selection
  toggleClaimsExperience(event) {
    const claimsExperienceId = event.target.dataset.claimsExperienceId
    const checkbox = event.target

    if (checkbox.checked) {
      this.selectedClaimsExperienceIds.add(claimsExperienceId)
    } else {
      this.selectedClaimsExperienceIds.delete(claimsExperienceId)
    }

    this.updateSelectionSummary()
  }

  // Update selection summary
  updateSelectionSummary() {
    const count = this.selectedClaimsExperienceIds.size
    
    if (count === 0) {
      this.selectionSummaryTarget.textContent = ""
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    } else {
      this.selectionSummaryTarget.textContent = `${count} claims experience${count === 1 ? "" : "s"} selected`
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  // Handle adding claims experiences to renewal
  async addToRenewal() {
    if (this.selectedClaimsExperienceIds.size === 0) {
      return
    }

    this.addButtonTarget.disabled = true
    this.addButtonTarget.textContent = "Adding..."

    try {
      const response = await fetch(`/workspaces/${this.workspaceIdValue}/renewals/${this.renewalIdValue}/add_claims_experiences`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        },
        body: JSON.stringify({
          claims_experience_ids: [...this.selectedClaimsExperienceIds]
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.showSuccessMessage(`Successfully added ${this.selectedClaimsExperienceIds.size} claims experience${this.selectedClaimsExperienceIds.size === 1 ? "" : "s"} to renewal`)
        this.close()

        // Refresh page to show updated data
        window.location.reload()
      } else {
        const errorData = await response.json()
        this.showErrorMessage(errorData.error || "Failed to add claims experiences to renewal")
      }
    } catch (error) {
      console.error("Error adding to renewal:", error)
      this.showErrorMessage("An error occurred while adding to renewal")
    }

    this.addButtonTarget.disabled = false
    this.addButtonTarget.textContent = "Add Selected Claims Experiences"
  }

  // Load claims experiences via AJAX
  async loadClaimsExperiences() {
    try {
      const params = new URLSearchParams({
        page: this.currentPage,
        search: this.currentSearch,
        renewal_id: this.renewalIdValue // Exclude claims experiences already in this renewal
      })

      const response = await fetch(`/workspaces/${this.workspaceIdValue}/claims_experiences.json?${params}`)
      const data = await response.json()

      this.renderClaimsExperiences(data)
    } catch (error) {
      console.error("Error loading claims experiences:", error)
      this.claimsExperiencesListTarget.innerHTML = "<p class=\"text-red-600 p-4\">Error loading claims experiences</p>"
    }
  }

  // Render claims experiences in the modal
  renderClaimsExperiences(data) {
    if (!data.claims_experiences || data.claims_experiences.length === 0) {
      this.claimsExperiencesListTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          ${this.currentSearch ? `No claims experiences found for "${this.currentSearch}"` : "No claims experiences available"}
        </div>
      `
      return
    }

    let html = "<div class=\"divide-y divide-gray-200\">"

    data.claims_experiences.forEach(claimsExperience => {
      const isSelected = this.selectedClaimsExperienceIds.has(claimsExperience.id.toString())
      const benefitType = claimsExperience.benefit_type || claimsExperience.id
      const billingDivision = claimsExperience.billing_division || "N/A"
      const period = claimsExperience.period || "N/A"
      
      html += `
        <div class="p-4 hover:bg-gray-50 border border-transparent">
          <div class="flex items-start space-x-3">
            <input type="checkbox" 
                   ${isSelected ? "checked" : ""}
                   data-claims-experience-id="${claimsExperience.id}"
                   data-action="change->claims-experience-selection-modal#toggleClaimsExperience"
                   class="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
            <div class="flex-1">
              <h4 class="font-medium text-gray-900">${benefitType}</h4>
              <p class="text-sm text-gray-500">
                Group: ${claimsExperience.client_name || "None"} •
                Billing Division: ${billingDivision} •
                Period: ${period}
              </p>
              <p class="text-sm text-gray-500 mt-1">
                Created: ${new Date(claimsExperience.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      `
    })

    html += "</div>"

    // Add pagination if needed
    if (data.total_pages > 1) {
      html += this.renderPagination(data)
    }

    this.claimsExperiencesListTarget.innerHTML = html
  }

  // Render pagination controls
  renderPagination(data) {
    let paginationHtml = `
      <div class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Showing ${((data.current_page - 1) * 10) + 1} to ${Math.min(data.current_page * 10, data.total_count)} of ${data.total_count} results
          </div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
    `

    if (data.current_page > 1) {
      paginationHtml += `
        <button data-page="${data.current_page - 1}"
                data-action="click->claims-experience-selection-modal#goToPage"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
          <span class="sr-only">Previous</span>
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      `
    }

    // Show page numbers (simplified - just show current page context)
    for (let i = Math.max(1, data.current_page - 2); i <= Math.min(data.total_pages, data.current_page + 2); i++) {
      const isFirst = i === Math.max(1, data.current_page - 2) && data.current_page <= 1
      const isLast = i === Math.min(data.total_pages, data.current_page + 2) && data.current_page >= data.total_pages
      const roundingClasses = (isFirst ? " rounded-l-md" : "") + (isLast ? " rounded-r-md" : "")
      const activeClass = i === data.current_page ? "bg-blue-50 text-blue-600 border-blue-300" : "bg-white text-gray-700 hover:bg-gray-50"

      paginationHtml += `
        <button data-page="${i}"
                data-action="click->claims-experience-selection-modal#goToPage"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${activeClass}${roundingClasses}">
          ${i}
        </button>
      `
    }

    if (data.current_page < data.total_pages) {
      paginationHtml += `
        <button data-page="${data.current_page + 1}"
                data-action="click->claims-experience-selection-modal#goToPage"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
          <span class="sr-only">Next</span>
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      `
    }

    paginationHtml += "</nav></div></div>"
    return paginationHtml
  }

  // Modal utility methods
  showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  resetModal() {
    this.currentPage = 1
    this.currentSearch = ""
    this.selectedClaimsExperienceIds.clear()
    this.searchInputTarget.value = ""
    this.selectionSummaryTarget.textContent = ""
    this.addButtonTarget.disabled = true
    this.addButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    this.claimsExperiencesListTarget.innerHTML = ""
  }

  showSuccessMessage(message) {
    // You can integrate with your flash message system here
    alert(message) // Temporary - replace with your flash message system
  }

  showErrorMessage(message) {
    // You can integrate with your flash message system here
    alert(message) // Temporary - replace with your flash message system
  }
}